/**
 * TakeoffExportModal — progress UI for deal-wide take-off PDF export.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Loader2 } from 'lucide-react';
import {
  downloadTakeoffExportBlob,
  exportTakeoffMarkupPdf,
  type TakeoffExportProgress,
} from '../../utils/takeoffExportPdf';
import './TakeoffExportModal.css';

export interface TakeoffExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  dealTitle?: string;
  contactName?: string;
  userName?: string;
  projectId?: string | null;
  hasLinks: boolean;
}

export function TakeoffExportModal({
  isOpen,
  onClose,
  dealId,
  dealTitle,
  contactName,
  userName,
  projectId,
  hasLinks,
}: TakeoffExportModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<TakeoffExportProgress | null>(null);

  if (!isOpen) return null;

  const handleExport = async () => {
    setBusy(true);
    setError(null);
    setProgress({ phase: 'loading', message: 'Starting export…' });
    try {
      const result = await exportTakeoffMarkupPdf({
        dealId,
        dealTitle,
        contactName,
        userName,
        projectId,
        onProgress: setProgress,
      });
      downloadTakeoffExportBlob(result.blob, result.filename);
      onClose();
    } catch (err) {
      console.error('Take-off PDF export failed:', err);
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  const progressPct =
    progress?.total && progress.current != null
      ? Math.round((progress.current / progress.total) * 100)
      : null;

  return createPortal(
    <div className="takeoff-export-overlay" onClick={() => !busy && onClose()}>
      <div
        className="takeoff-export-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-labelledby="takeoff-export-title"
      >
        <div className="takeoff-export-header">
          <h2 id="takeoff-export-title">Export Internal Take-off PDF</h2>
          <button
            type="button"
            className="takeoff-export-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="takeoff-export-body">
          <p>
            Generates an <strong>internal-use-only</strong> PDF of take-off markup for all plan files on
            this deal — cropped plan snippets and timber tables for estimating. Not for client distribution.
          </p>
          {!hasLinks && (
            <p className="takeoff-export-warning">No take-off items on this document yet.</p>
          )}
          {error && <p className="takeoff-export-error">{error}</p>}
          {busy && progress && (
            <div className="takeoff-export-progress">
              <Loader2 size={16} className="spin" />
              <span>{progress.message}</span>
              {progressPct != null && progress.phase === 'snippets' && (
                <div className="takeoff-export-progress-bar">
                  <div className="takeoff-export-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="takeoff-export-footer">
          <button type="button" className="takeoff-export-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="takeoff-export-run"
            onClick={() => void handleExport()}
            disabled={busy}
          >
            {busy ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
            {busy ? 'Exporting…' : 'Export Internal PDF'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default TakeoffExportModal;
